import type { Archive, AutoContinuePayload } from "../../domain/types";
import type { FeatureRoutingSettings } from "../featureRoutes";
import {
  beginPendingTurn,
  beginTurnTrace,
  createTurnCheckpoint,
  putTurnReplayContext,
  updatePendingTurn,
  enterTurnTraceStage,
  finishTurnTrace,
} from "../db";
import { prepareTurn } from "./preTurn";
import { gatherContext } from "./contextGathering";
import { runMainChat } from "./mainChat";
import { applyPostTurn } from "./postTurn";
import { runOptionalSideRequests, settleTurn } from "./settlement";
import {
  preparePlotDirector,
  settlePlotEvolution,
} from "../../features/plot-evolution/service";
export async function sendMessage(
  archive: Archive,
  input: string,
  settings: FeatureRoutingSettings,
  onToken: (s: string) => void,
  signal?: AbortSignal,
  onMainCommitted?: (archive: Archive) => Promise<void>,
  options: {
    autoContinue?: AutoContinuePayload;
    onPhase?: (phase: string) => void;
    lab?: {
      cachedContext?: { rag: string; memory: string; guidance: string };
    };
  } = {},
) {
  const effectiveInput = options.autoContinue?.prompt ?? input;
  const turn = prepareTurn(archive, effectiveInput);
  const checkpoint = await createTurnCheckpoint(archive, turn.turnId);
  await beginPendingTurn({
    id: turn.turnId,
    archiveName: archive.name,
    input: turn.input,
    startedAt: Date.now(),
    phase: "prepared",
  });
  await beginTurnTrace(turn.turnId, archive.name, turn.input.length).catch(() => undefined);
  try {
    await updatePendingTurn(turn.turnId, { phase: "directing" });
    await enterTurnTraceStage(turn.turnId, "directing").catch(() => undefined);
    options.onPhase?.("准备剧情导演");
    const directed = options.lab?.cachedContext
      ? { archive: turn.frozen, directive: "" }
      : await preparePlotDirector(turn.frozen, turn.input, settings, signal).catch(() => ({ archive: turn.frozen, directive: "" }));
    await updatePendingTurn(turn.turnId, { phase: "retrieving" });
    await enterTurnTraceStage(turn.turnId, "retrieving").catch(() => undefined);
    options.onPhase?.("检索上下文");
    const context = options.lab?.cachedContext ?? (await gatherContext(directed.archive, turn.input, settings, signal));
    if (directed.directive) context.guidance = [context.guidance, `【本轮剧情导演】\n${directed.directive}`].filter(Boolean).join("\n\n");
    await putTurnReplayContext({ id: `context:${archive.archiveId}:${turn.turnId}`, archiveName: archive.name, turnId: turn.turnId, input: turn.input, createdAt: Date.now(), context: structuredClone(context) });
    await updatePendingTurn(turn.turnId, { phase: "main-streaming" });
    await enterTurnTraceStage(turn.turnId, "main-streaming").catch(() => undefined);
    options.onPhase?.("编排主叙事");
    const raw = await runMainChat(directed.archive, turn.input, context, settings, onToken, signal, { archiveName: archive.name, turnId: turn.turnId });
    await updatePendingTurn(turn.turnId, { phase: "main-complete", assistantText: raw });
    await enterTurnTraceStage(turn.turnId, "main-complete").catch(() => undefined);
    options.onPhase?.("提交主叙事");
    const post = applyPostTurn(directed.archive, input || turn.input, raw, { turnId: turn.turnId, snapshotId: checkpoint.id, autoContinue: options.autoContinue });
    await onMainCommitted?.(post.archive);
    await updatePendingTurn(turn.turnId, { phase: "settling" });
    await enterTurnTraceStage(turn.turnId, "settling").catch(() => undefined);
    options.onPhase?.("结算与演化");
    const settled = await settleTurn(post.archive, turn.input, post.visible, settings);
    settled.quickChat.pendingConversationIds = [];
    await updatePendingTurn(turn.turnId, { phase: "evolving" });
    await enterTurnTraceStage(turn.turnId, "evolving").catch(() => undefined);
    const finalized = await settlePlotEvolution(settled, turn.input, post.visible, settings).catch(() => settled);
    await runOptionalSideRequests();
    await updatePendingTurn(turn.turnId, { phase: "complete" });
    await finishTurnTrace(turn.turnId, "ok").catch(() => undefined);
    options.onPhase?.("完成");
    return { ...post, archive: finalized, turnId: turn.turnId };
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === "AbortError";
    await updatePendingTurn(turn.turnId, { phase: aborted ? "aborted" : "failed", error: error instanceof Error ? error.message : String(error) }).catch(() => undefined);
    await finishTurnTrace(turn.turnId, aborted ? "aborted" : "error", error instanceof Error ? error.message : String(error)).catch(() => undefined);
    throw error;
  }
}
